import React, { useEffect, useRef } from 'react';

import {
  Button,
  IconArrowBackIosNewLight24,
  IconButton,
  IconCheckIndeterminateSmallLight24,
  IconKeyboardControlKeyLight24,
  IconLightspeedLight24,
} from '@lightspeed/unified-components-default-consumer-theme/react';
import { T, useT } from '@transifex/react';

interface WidgetHeaderProps {
  isOpen: boolean;
  showBack: boolean;
  onBack: () => void;
  onOpen: () => void;
  onClose: () => void;
}

export function WidgetHeader({ isOpen, showBack, onBack, onOpen, onClose }: WidgetHeaderProps) {
  const t = useT();
  const headerRef = useRef<HTMLDivElement>(null);

  const handleClick = () => {
    if (!isOpen) return onOpen();

    return onClose();
  };

  useEffect(() => {
    const header = headerRef.current;

    if (!header) return;

    document.body.style.paddingTop = `${header.clientHeight}px`;
  }, []);

  return (
    <div
      ref={headerRef}
      className="fixed top-0 left-0 right-0 z-[2] flex border-b-sm border-neutral-soft bg-app-primary"
    >
      {showBack && (
        <div className="border-r-sm border-neutral-soft">
          <IconButton
            type="button"
            ariaLabel={t('Back', { _context: 'Back button text', _tags: 'reservation,navigation' })}
            appearance="ghost"
            size="large"
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              onBack();
            }}
          >
            <IconArrowBackIosNewLight24 className="size-4" />
          </IconButton>
        </div>
      )}
      <Button
        type="button"
        appearance="ghost"
        size={isOpen ? 'large' : 'x-large'}
        block
        onClick={handleClick}
        customClasses={{
          content: 'w-full text-neutral-default',
          root: `p-0 ${isOpen ? 'rounded-b-none' : ''}`,
        }}
        customDataAttributes={{ root: { testid: 'widget-header-button' } }}
      >
        <div className="flex items-center justify-between w-full px-4 h-full">
          <div className="flex items-center gap-2">
            {!isOpen && <IconLightspeedLight24 className="ls-logo" />}
            <T
              _str="Book a table"
              _context="Header title for reservation widget"
              _tags="widget"
              _key="Widget.Header.BookTable.button"
            />
          </div>
          {isOpen ? <IconCheckIndeterminateSmallLight24 /> : <IconKeyboardControlKeyLight24 />}
        </div>
      </Button>
    </div>
  );
}
